import React, { useState, useEffect } from 'react';
import { api, getStation } from '../api';

const SHOW_TYPES = [
  { id:'this_day',      label:'This Day in History',    duration:'~3-4 min',  freq:'Daily',   icon:'📅', desc:'3-4 notable events on this date, AI-hosted', available:true },
  { id:'countdown',     label:'Daily Countdown',        duration:'25-35 min', freq:'Daily',   icon:'🏆', desc:'Top 10 songs in your format with AI host commentary', available:false },
  { id:'community',     label:'Community Spotlight',    duration:'30 min',    freq:'Weekly',  icon:'🏘️', desc:'Local news and 3 community interest stories', available:false },
  { id:'throwback',     label:'Weekend Throwback Mix',  duration:'60 min',    freq:'Weekly',  icon:'⏪', desc:'Themed decade or genre retrospective with commentary', available:false },
  { id:'news_briefing', label:'Industry News Briefing', duration:'10 min',    freq:'Weekly',  icon:'📰', desc:'Deep dive on topics relevant to your audience', available:false },
  { id:'morning_motivation', label:'Morning Motivation', duration:'5 min',    freq:'Daily',   icon:'☀️', desc:'Daily inspirational segment (Gospel/wellness stations)', available:false },
];

// eslint-disable-next-line no-unused-vars
const DELIVERY = ["sftp","rss","api","email"];

export default function ShowFactory() {
  const station = getStation();
  const [shows, setShows]         = useState([]);
  const [config, setConfig]       = useState({ show_mix:[], delivery_method:'rss', host_voice_a:'voice_neutral', host_voice_b:'voice_warm_female', content_rules:'family_safe' });
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(true);
  const [ordering, setOrdering]   = useState(false);
  const [activeTab, setActiveTab] = useState('shows');
  const set = (k,v) => setConfig(p => ({...p,[k]:v}));

  const toggleShowType = (id) => {
    setConfig(p => ({
      ...p,
      show_mix: p.show_mix.includes(id) ? p.show_mix.filter(s => s!==id) : [...p.show_mix, id]
    }));
  };

  const stationId = station?.id;
  useEffect(() => {
    if (!stationId) { setLoading(false); return; }
    Promise.all([
      api.get(`/broadcast/shows?station_id=${stationId}`).catch(() => ({ shows:[] })),
      api.get(`/broadcast/shows/config/${stationId}`).catch(() => null),
    ]).then(([sh, cfg]) => {
      setShows(sh?.shows || []);
      if (cfg) setConfig(c => ({...c,...cfg}));
    }).finally(() => setLoading(false));
  }, [stationId]);

  const orderShow = async (show_format) => {
    setOrdering(true);
    try {
      await api.post('/broadcast/shows/order', { station_id: station.id, show_format });
      const s = await api.get(`/broadcast/shows?station_id=${station.id}`).catch(() => ({ shows:[] }));
      setShows(s?.shows || []);
    } catch(e) { alert(e.message); }
    finally { setOrdering(false); }
  };

  const saveConfig = async () => {
    try { await api.post(`/broadcast/shows/config/${station.id}`, config); alert('Configuration saved!'); }
    catch(e) { alert(e.message); }
  };

  const Tab = ({ id, label }) => (
    <button onClick={() => setActiveTab(id)} style={{ padding:'8px 16px', fontSize:13, fontWeight:600, border:'none', cursor:'pointer', borderRadius:8, background: activeTab===id ? '#6366f1' : 'transparent', color: activeTab===id ? '#fff' : '#64748b' }}>{label}</button>
  );

  const statusColor = { generating:'#f59e0b', qc:'#6366f1', ready:'#22c55e', delivered:'#10b981', error:'#ef4444' };

  if (!station) return (
    <div style={{ textAlign:'center', padding:'60px 20px', background:'#fff', borderRadius:16, border:'2px dashed #e2e8f0' }}>
      <div style={{ fontSize:48, marginBottom:12 }}>🎚️</div>
      <h3 style={{ margin:'0 0 8px', color:'#0f172a' }}>No station selected</h3>
      <p style={{ color:'#64748b', fontSize:14 }}>Go to <strong>My Stations</strong> and set an active station first.</p>
    </div>
  );

  return (
    <div>
      <div style={{ background:'linear-gradient(135deg,#0f172a,#1e293b)', borderRadius:12, padding:'16px 20px', marginBottom:20, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <div style={{ color:'#fff', fontWeight:700, fontSize:15 }}>🎚️ Show Factory · {station.callsign}</div>
          <div style={{ color:'rgba(255,255,255,0.5)', fontSize:12, marginTop:2 }}>AI-produced, broadcast-ready shows delivered on schedule</div>
        </div>
        <span style={{ fontSize:12, fontWeight:600, padding:'4px 12px', borderRadius:20, background:'rgba(34,197,94,0.15)', color:'#4ade80', border:'1px solid rgba(34,197,94,0.25)' }}>
          {config.show_mix?.length || 0} show types active
        </span>
      </div>

      <div style={{ display:'flex', gap:4, marginBottom:20, background:'#f8fafc', padding:4, borderRadius:10, width:'fit-content' }}>
        <Tab id="shows"   label="Show Library" />
        <Tab id="catalog" label="Order a Show" />
        <Tab id="config"  label="Delivery Config" />
      </div>

      {activeTab === 'shows' && (
        <div>
          {shows.length === 0 ? (
            <div style={{ textAlign:'center', padding:'50px 20px', background:'#fff', borderRadius:12, border:'2px dashed #e2e8f0' }}>
              <div style={{ fontSize:40, marginBottom:10 }}>🎚️</div>
              <h3 style={{ margin:'0 0 8px', color:'#0f172a' }}>No shows yet</h3>
              <p style={{ color:'#64748b', fontSize:13 }}>Go to <strong>Order a Show</strong> to commission your first AI-produced show.</p>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {shows.map((s, i) => (
                <div key={i} style={{ background:'#fff', borderRadius:12, padding:'16px 20px', border:'1px solid #f1f5f9', display:'flex', alignItems:'center', gap:16 }}>
                  <div style={{ width:44, height:44, borderRadius:10, background:'#f8fafc', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}>
                    {SHOW_TYPES.find(t => t.id===s.show_format)?.icon || '🎵'}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, fontSize:14, color:'#0f172a' }}>{s.title || SHOW_TYPES.find(t => t.id===s.show_format)?.label}</div>
                    <div style={{ fontSize:12, color:'#64748b' }}>Episode {s.episode_number || 1} · {s.duration_seconds ? `${Math.round(s.duration_seconds/60)} min` : 'Generating…'} · Air date: {s.air_date || 'TBD'}</div>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:20, background:`${(statusColor[s.status]||'#94a3b8')}18`, color: statusColor[s.status]||'#94a3b8' }}>{s.status}</span>
                    {s.status === 'ready' && s.audio_url && <button onClick={() => window.open(s.audio_url, '_blank', 'noopener')} style={{ padding:'6px 14px', borderRadius:6, border:'1.5px solid #6366f1', background:'#fff', color:'#6366f1', fontSize:12, fontWeight:700, cursor:'pointer' }}>Download</button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'catalog' && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:14 }}>
          {SHOW_TYPES.map(type => (
            <div key={type.id} style={{ background:'#fff', borderRadius:12, padding:20, border:'1px solid #f1f5f9' }}>
              <div style={{ fontSize:28, marginBottom:10 }}>{type.icon}</div>
              <div style={{ fontWeight:700, fontSize:14, color:'#0f172a', marginBottom:3 }}>{type.label}</div>
              <div style={{ fontSize:12, color:'#64748b', marginBottom:8, lineHeight:1.4 }}>{type.desc}</div>
              <div style={{ display:'flex', gap:6, marginBottom:14 }}>
                <span style={{ fontSize:11, padding:'3px 8px', borderRadius:6, background:'#eef2ff', color:'#4f46e5', fontWeight:600 }}>{type.duration}</span>
                <span style={{ fontSize:11, padding:'3px 8px', borderRadius:6, background:'#f0fdf4', color:'#15803d', fontWeight:600 }}>{type.freq}</span>
              </div>
              <button onClick={() => type.available && orderShow(type.id)} disabled={ordering || !type.available} style={{ width:'100%', padding:'9px', borderRadius:8, border:'none', background: !type.available ? '#e2e8f0' : (ordering ? '#c7d2fe' : 'linear-gradient(135deg,#6366f1,#4f46e5)'), color: !type.available ? '#94a3b8' : '#fff', fontWeight:700, fontSize:12, cursor: (ordering || !type.available) ? 'not-allowed' : 'pointer' }}>
                {!type.available ? 'Coming soon' : (ordering ? 'Ordering…' : 'Order This Show')}
              </button>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'config' && (
        <div style={{ background:'#fff', borderRadius:12, padding:24, border:'1px solid #f1f5f9', maxWidth:600 }}>
          <h3 style={{ margin:'0 0 18px', fontSize:15, fontWeight:700, color:'#0f172a' }}>Delivery & Production Config</h3>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div>
              <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#374151', marginBottom:4 }}>Delivery Method</label>
              <select value={config.delivery_method} onChange={e => set('delivery_method', e.target.value)} style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none' }}>
                <option value="sftp">SFTP Drop-in (auto-schedule in playout system)</option>
                <option value="rss">Private RSS Feed (subscribe in playout system)</option>
                <option value="api">API Pull (your system polls for new shows)</option>
                <option value="email">Email Download Link (manual)</option>
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#374151', marginBottom:4 }}>Content Filter Level</label>
              <select value={config.content_rules} onChange={e => set('content_rules', e.target.value)} style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none' }}>
                <option value="family_safe">Family Safe</option>
                <option value="standard">Standard</option>
                <option value="after_watershed">After Watershed</option>
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#374151', marginBottom:8 }}>Active Show Types</label>
              <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                {SHOW_TYPES.map(t => (
                  <button key={t.id} onClick={() => toggleShowType(t.id)} style={{
                    padding:'5px 12px', borderRadius:20, border:`1.5px solid ${config.show_mix?.includes(t.id) ? '#6366f1' : '#e2e8f0'}`,
                    background: config.show_mix?.includes(t.id) ? '#eef2ff' : '#fff',
                    color: config.show_mix?.includes(t.id) ? '#4f46e5' : '#374151',
                    fontSize:12, fontWeight:600, cursor:'pointer'
                  }}>{t.icon} {t.label}</button>
                ))}
              </div>
            </div>
            <button onClick={saveConfig} style={{ alignSelf:'flex-end', padding:'10px 24px', borderRadius:8, border:'none', background:'linear-gradient(135deg,#6366f1,#4f46e5)', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer' }}>
              Save Config
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
